import {
  Tr,
  Td,
  VStack,
  Flex,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import config from 'configs/app';
import dayjs from 'lib/date/dayjs';
import getCurrencyValue from 'lib/getCurrencyValue';
import { useI18n } from 'lib/i18n/context';
import AddressFromTo from 'ui/shared/address/AddressFromTo';
import Skeleton from 'ui/shared/chakra/Skeleton';
import Tag from 'ui/shared/chakra/Tag';
import CurrencyValue from 'ui/shared/CurrencyValue';
import BlockEntity from 'ui/shared/entities/block/BlockEntity';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import FinanceStatusTag from 'ui/shared/statusTag/FinanceStatusTag';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';
import TxFee from 'ui/shared/tx/TxFee';
import TxWatchListTags from 'ui/shared/tx/TxWatchListTags';
import TxAdditionalInfo from 'ui/txs/TxAdditionalInfo';

import TxTranslationType from './TxTranslationType';
import TxType from './TxType';

type Props = {
  tx: Transaction;
  showBlockInfo: boolean;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  variant?: 'default' | 'explorer';
};

const TxsTableItem = ({ tx, showBlockInfo, currentAddress, enableTimeIncrement, isLoading, variant = 'default' }: Props) => {
  const { t } = useI18n();
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const isExplorer = variant === 'explorer';
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const rowBgColor = useColorModeValue('#FFFFFF', '#111111');
  const primaryTextColor = useColorModeValue('#000000', '#FFFFFF');
  const secondaryTextColor = useColorModeValue('#616161', '#999999');
  const methodTagBgColor = useColorModeValue('#F7F7F7', '#171717');
  const methodTagBorderColor = useColorModeValue('#E2E8F0', '#222222');
  const methodTagTextColor = useColorModeValue('#2D3748', '#FFFFFF');
  const amountStr = getCurrencyValue({ value: tx.value, decimals: '18', accuracy: 8 }).valueStr;

  return (
    <Tr
      as={ motion.tr }
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transitionDuration="normal"
      transitionTimingFunction="linear"
      key={ tx.hash }
      borderRadius={ isExplorer ? 0 : undefined }
      sx={ isExplorer ? {
        '& td': {
          borderTop: 'none',
          borderBottom: `1px solid ${ borderColor }`,
          bgColor: rowBgColor,
          py: 4,
          '&:first-child': {
            borderLeft: 'none',
          },
          '&:last-child': {
            borderRight: 'none',
          },
        },
      } : {
        '& td': {
          borderTop: 'none',
          borderBottom: `1px solid ${ borderColor }`,
          bgColor: rowBgColor,
        },
      } }
    >
      { /* Txn hash */ }
      <Td pl={ 4 }>
        { isExplorer ? (
          <Flex alignItems="center" gap="4px">
            <TxAdditionalInfo tx={ tx } isLoading={ isLoading }/>
            <TxEntity
              hash={ tx.hash }
              isLoading={ isLoading }
              color="#FFFFFF"
              fontFamily="'Oxanium', var(--chakra-fonts-body)"
              fontSize="14px"
              lineHeight="20px"
              fontWeight={ 500 }
              noIcon
              maxW="100%"
              truncation="constant_long"
            />
          </Flex>
        ) : (
          <Flex alignItems="start" gap="8px">
            <TxAdditionalInfo tx={ tx } isLoading={ isLoading }/>
            <VStack alignItems="start" spacing="4px">
              <TxEntity
                hash={ tx.hash }
                isLoading={ isLoading }
                color={ isExplorer ? '#FFFFFF' : primaryTextColor }
                fontFamily="'Oxanium', var(--chakra-fonts-body)"
                fontSize="14px"
                lineHeight="20px"
                fontWeight={ 500 }
                noIcon
                maxW="100%"
                truncation="constant_long"
              />
              { tx.status_label ? (
                <FinanceStatusTag status={ tx.status_label } isLoading={ isLoading }/>
              ) : (
                <TxStatus status={ tx.status } errorText={ tx.status === 'error' ? tx.result : undefined } isLoading={ isLoading }/>
              ) }
            </VStack>
          </Flex>
        ) }
      </Td>
      { isExplorer ? (
        <>
          { /* Type */ }
          <Td pl={ 4 }>
            <VStack alignItems="start">
              { tx.translation ? (
                <TxTranslationType
                  types={ tx.transaction_types }
                  isLoading={ isLoading || tx.translation.isLoading }
                  translatationType={ tx.translation.data?.type }
                  variant={ variant }
                />
              ) :
                <TxType types={ tx.transaction_types } isLoading={ isLoading } variant={ variant }/>
              }
              <TxStatus status={ tx.status } errorText={ tx.status === 'error' ? tx.result : undefined } isLoading={ isLoading } variant={ variant }/>
              <TxWatchListTags tx={ tx } isLoading={ isLoading }/>
            </VStack>
          </Td>
          { /* Method */ }
          <Td whiteSpace="nowrap">
            { tx.method && (
              <Tag
                colorScheme={ tx.method === 'Multicall' ? 'teal' : 'gray' }
                isLoading={ isLoading }
                isTruncated
                bgColor="#171717"
                borderWidth="1px"
                borderColor="#222222"
                color="#FFFFFF"
                fontSize="12px"
                fontWeight={ 500 }
                lineHeight="16px"
                fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                borderRadius="4px"
                px="8px"
                py="2px"
                minH="24px"
              >
                { tx.method }
              </Tag>
            ) }
          </Td>
          { /* Block */ }
          { showBlockInfo && (
            <Td>
              { tx.block_number && (
                <BlockEntity
                  isLoading={ isLoading }
                  number={ tx.block_number }
                  noIcon
                  fontSize="sm"
                  lineHeight={ 6 }
                  fontWeight={ 500 }
                  fontFamily="'Oxanium', var(--chakra-fonts-body)"
                  color="#FFFFFF"
                />
              ) }
            </Td>
          ) }
          { /* Age */ }
          <Td>
            <TimeAgoWithTooltip
              timestamp={ tx.timestamp }
              enableIncrement={ enableTimeIncrement }
              isLoading={ isLoading }
              color="#999999"
              fontSize="14px"
              lineHeight="24px"
              fontWeight="500"
              fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
            />
          </Td>
          { /* From/To */ }
          <Td>
            <AddressFromTo
              from={ tx.from }
              to={ dataTo }
              current={ currentAddress }
              isLoading={ isLoading }
              mt="2px"
              mode="compact"
              variant="explorer"
              fromLabel={ t('txs.from') }
              toLabel={ t('txs.to') }
            />
          </Td>
          { !config.UI.views.tx.hiddenFields?.value && (
            <Td isNumeric color="#999999" fontFamily="'Oxanium', var(--chakra-fonts-body)" fontSize="14px" lineHeight="24px" fontWeight={ 500 }>
              <CurrencyValue value={ tx.value } accuracy={ 8 } isLoading={ isLoading }/>
            </Td>
          ) }
          { !config.UI.views.tx.hiddenFields?.tx_fee && (
            <Td isNumeric maxW="220px" color="#999999" fontFamily="'Oxanium', var(--chakra-fonts-body)" fontSize="14px" lineHeight="24px" fontWeight={ 500 }>
              <TxFee
                tx={ tx }
                accuracy={ 8 }
                isLoading={ isLoading }
                withCurrency={ Boolean(tx.celo || tx.stability_fee) }
                justifyContent="end"
              />
            </Td>
          ) }
        </>
      ) : (
        <>
          { /* Date Time */ }
          <Td verticalAlign="middle">
            <Skeleton isLoaded={ !isLoading }>
              <VStack spacing="0" alignItems="start">
                <Text
                  fontSize="14px"
                  fontWeight={ 500 }
                  color={ secondaryTextColor }
                  fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                  lineHeight="24px"
                >
                  { tx.timestamp ? dayjs(tx.timestamp).format('YYYY/MM/DD') : '-' }
                </Text>
                <Text
                  fontSize="14px"
                  fontWeight={ 500 }
                  color={ secondaryTextColor }
                  fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                  lineHeight="24px"
                >
                  { tx.timestamp ? dayjs(tx.timestamp).format('HH:mm:ss') : '-' }
                </Text>
              </VStack>
            </Skeleton>
          </Td>
          { /* Block */ }
          { showBlockInfo && (
            <Td verticalAlign="middle">
              { tx.block_number ? (
                <BlockEntity
                  isLoading={ isLoading }
                  number={ tx.block_number }
                  noIcon
                  fontSize="14px"
                  lineHeight="24px"
                  fontWeight={ 500 }
                  fontFamily="'Oxanium', var(--chakra-fonts-body)"
                  color={ primaryTextColor }
                />
              ) : (
                <Skeleton isLoaded={ !isLoading } display="inline-block">
                  <Text fontSize="14px" fontWeight={ 500 } color={ secondaryTextColor } fontFamily="'Oxanium', var(--chakra-fonts-body)">-</Text>
                </Skeleton>
              ) }
            </Td>
          ) }
          { /* From/To */ }
          <Td overflow="hidden">
            <AddressFromTo
              from={ tx.from }
              to={ dataTo }
              current={ currentAddress }
              isLoading={ isLoading }
              mt="2px"
              mode="compact"
              variant="explorer"
              fromLabel={ t('txs.from') }
              toLabel={ t('txs.to') }
            />
          </Td>
          { /* Amount */ }
          <Td verticalAlign="middle">
            <Skeleton isLoaded={ !isLoading } display="inline-block">
              <Text
                fontSize="14px"
                fontWeight={ 500 }
                color={ secondaryTextColor }
                fontFamily="'Oxanium', var(--chakra-fonts-body)"
                lineHeight="24px"
                wordBreak="break-all"
              >
                { amountStr || '-' }
              </Text>
            </Skeleton>
          </Td>
          { /* Method */ }
          <Td whiteSpace="nowrap" verticalAlign="middle">
            { tx.method && (
              <Tag
                colorScheme="gray"
                isLoading={ isLoading }
                isTruncated
                bgColor={ methodTagBgColor }
                borderWidth="1px"
                borderColor={ methodTagBorderColor }
                color={ methodTagTextColor }
                fontSize="12px"
                fontWeight={ 500 }
                lineHeight="16px"
                fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                borderRadius="4px"
                px="8px"
                py="2px"
                minH="24px"
              >
                { tx.method }
              </Tag>
            ) }
          </Td>
          { /* Value(USD) */ }
          <Td isNumeric verticalAlign="middle">
            <Skeleton isLoaded={ !isLoading } display="inline-block">
              <Text
                fontSize="14px"
                fontWeight={ 500 }
                color={ secondaryTextColor }
                fontFamily="'Oxanium', var(--chakra-fonts-body)"
                lineHeight="24px"
              >
                { tx.value_usd || 'N/A' }
              </Text>
            </Skeleton>
          </Td>
          { /* Txn Fee(BOT) */ }
          <Td isNumeric pr={ 5 } verticalAlign="middle">
            <TxFee
              tx={ tx }
              accuracy={ 8 }
              isLoading={ isLoading }
              color={ secondaryTextColor }
              fontFamily="'Oxanium', var(--chakra-fonts-body)"
              fontSize="14px"
              fontWeight={ 500 }
              justifyContent="end"
            />
          </Td>
        </>
      ) }
    </Tr>
  );
};

export default React.memo(TxsTableItem);
